import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";

/**
 * AutoResizeTextarea component that adjusts its height based on content
 * @component
 */
const AutoResizeTextarea = ({
  value,
  onChange,
  disabled,
  dir,
  testId,
  minHeight = 36,
  maxHeight,
  className,
  style,
  reCalculateHeight,
  totalCols,
  ...otherProps
}) => {
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to allow proper scrollHeight calculation
    textarea.style.height = "auto";

    // Calculate new height based on scrollHeight
    const newHeight = Math.max(minHeight, textarea.scrollHeight);

    // Apply max height constraint if provided
    const finalHeight = maxHeight ? Math.min(newHeight, maxHeight) : newHeight;

    textarea.style.height = `${finalHeight}px`;
  };

  // Adjust height on initial render and when value changes
  // Added setTimeout to ensure that height is calculated after DOM layout & paint
  useEffect(() => {
    const handleResize = setTimeout(() => {
      adjustHeight();
    }, 0);

    return () => clearTimeout(handleResize);
  }, [value, reCalculateHeight, totalCols]);

  // Adjust height on window resize
  useEffect(() => {
    const handleResize = _.debounce(() => {
      adjustHeight();
    }, 100);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel(); // Cancel any pending debounced calls
    };
  }, []);

  const handleInput = e => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleInput}
      disabled={disabled}
      dir={dir}
      data-test-id={testId}
      className={className}
      style={{
        overflow: "hidden",
        resize: "none",
        // Maintain text color even when disabled
        opacity: 1,
        ...style,
      }}
      {...otherProps}
    />
  );
};

AutoResizeTextarea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  dir: PropTypes.string,
  testId: PropTypes.string,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  totalCols: PropTypes.number,
};

export default AutoResizeTextarea;
